'use client';

import React, {
  Children,
  cloneElement,
  isValidElement,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';

import styles from './ParagraphRevealStandalone.module.css';

/**
 * Word-by-word paragraph reveal that measures its own position on every frame
 * instead of registering a ScrollTrigger. It therefore never depends on the
 * page-wide ScrollTrigger.refresh() cycle, which on this page runs before the
 * pinned hero, Lenis proxy and late-loading images have settled the layout.
 *
 * Words are rendered by React (not DOM-mutated) so parent re-renders cannot
 * wipe the spans the animation is driving. Markup such as strong, em, and a
 * is kept in place.
 */

const EDGE_FRACTIONS = { top: 0, center: 0.5, bottom: 1 };
const WORD_ATTRIBUTE = 'data-reveal-word';
const VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

const clamp01 = value => (value < 0 ? 0 : value > 1 ? 1 : value);

const resolveEdge = (token, size) => {
  if (typeof token === 'number') return token;
  const value = String(token ?? '').trim();
  if (value in EDGE_FRACTIONS) return EDGE_FRACTIONS[value] * size;
  if (value.endsWith('%')) return (parseFloat(value) / 100) * size;
  const numeric = parseFloat(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

/**
 * Distance in pixels between the element edge and the viewport edge described
 * by a GSAP-style position string such as "top 80%". Zero when they meet.
 */
const edgeDistance = (spec, fallback, rect, viewportHeight) => {
  const [elementEdge = 'top', viewportEdge = 'top'] = String(spec || fallback)
    .trim()
    .split(/\s+/);
  return (
    rect.top + resolveEdge(elementEdge, rect.height) - resolveEdge(viewportEdge, viewportHeight)
  );
};

/**
 * The wrapper is display:contents so it stays out of the layout, which also
 * means it has no box of its own to measure. Fall back to the area its children
 * cover in that case.
 */
const measureRect = element => {
  const rect = element.getBoundingClientRect();
  if (rect.width > 0 || rect.height > 0) return rect;

  const childRects = Array.from(element.children).map(child => child.getBoundingClientRect());
  if (childRects.length === 0) return rect;

  const top = Math.min(...childRects.map(child => child.top));
  const bottom = Math.max(...childRects.map(child => child.bottom));
  return { top, bottom, height: bottom - top };
};

const buildWordNodes = text => {
  if (!text) return [];

  const nodes = [];
  let key = 0;

  String(text)
    .split(/(\n+)/)
    .forEach(block => {
      if (!block) return;

      if (/^\n+$/.test(block)) {
        block.split('').forEach(() => {
          nodes.push(<br key={`br-${key++}`} />);
        });
        return;
      }

      block.split(/(\s+)/).forEach(part => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          // A regular space, never &nbsp;: the browser needs a break
          // opportunity here, and the space has to collapse when the line
          // wraps rather than hang beside the last word.
          nodes.push(' ');
          return;
        }
        nodes.push(
          <span key={`w-${key++}`} className={styles.word} data-reveal-word="">
            {part}
          </span>
        );
      });
    });

  return nodes;
};

/**
 * Wrap every word of every text node so markup (strong, em, a) stays in place
 * while the reveal can still target individual words.
 */
const wrapWordsInChildren = children =>
  Children.map(children, child => {
    if (child == null || typeof child === 'boolean') {
      return child;
    }
    if (typeof child === 'string' || typeof child === 'number') {
      return buildWordNodes(String(child));
    }
    if (!isValidElement(child)) {
      return child;
    }
    if (child.props?.['data-reveal-word'] !== undefined) {
      return child;
    }
    if (typeof child.type === 'string' && VOID_TAGS.has(child.type)) {
      return child;
    }
    if (child.props.children == null) {
      return child;
    }
    return cloneElement(child, null, wrapWordsInChildren(child.props.children));
  });

const animateWords = ({
  element,
  start,
  end,
  staggerEach,
  wordDuration,
  delay,
  once,
  dimOpacity,
}) => {
  const words = Array.from(element.querySelectorAll(`[${WORD_ATTRIBUTE}]`));
  if (words.length === 0) return () => {};

  const totalDuration = delay + (words.length - 1) * staggerEach + wordDuration;
  const windows = words.map((_, index) => {
    const from = (delay + index * staggerEach) / totalDuration;
    return { from, span: wordDuration / totalDuration || 1 };
  });
  const rendered = words.map(() => -1);

  let frameId = null;
  let isSettled = false;
  let observer = null;

  const applyProgress = progress => {
    words.forEach((word, index) => {
      const { from, span } = windows[index];
      const local = clamp01((progress - from) / span);
      const eased = 1 - (1 - local) * (1 - local);
      const opacity = dimOpacity + (1 - dimOpacity) * eased;
      if (Math.abs(opacity - rendered[index]) < 0.005) return;
      rendered[index] = opacity;
      word.style.opacity = local >= 1 ? '' : String(opacity);
    });
  };

  const update = () => {
    if (isSettled) return;
    const rect = measureRect(element);
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const fromStart = edgeDistance(start, 'top 85%', rect, viewportHeight);
    const fromEnd = edgeDistance(end, 'top 60%', rect, viewportHeight);
    const travel = fromStart - fromEnd;
    const progress = travel === 0 ? (fromStart <= 0 ? 1 : 0) : clamp01(fromStart / travel);

    applyProgress(progress);

    if (once && progress >= 1) {
      isSettled = true;
      observer?.disconnect();
      observer = null;
    }
  };

  const tick = () => {
    update();
    if (isSettled) {
      frameId = null;
      return;
    }
    frameId = window.requestAnimationFrame(tick);
  };

  const startLoop = () => {
    if (isSettled) return;
    if (frameId == null) frameId = window.requestAnimationFrame(tick);
  };

  const stopLoop = () => {
    if (frameId != null) window.cancelAnimationFrame(frameId);
    frameId = null;
  };

  update();
  startLoop();

  const childElements = Array.from(element.children);
  const observedElements =
    childElements.length > 1
      ? [childElements[0], childElements[childElements.length - 1]]
      : childElements;

  if (typeof IntersectionObserver !== 'undefined' && observedElements.length > 0) {
    const visibleElements = new Set();

    observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            visibleElements.add(entry.target);
          } else {
            visibleElements.delete(entry.target);
          }
        });

        update();

        if (visibleElements.size > 0) {
          startLoop();
        } else {
          stopLoop();
        }
      },
      { rootMargin: '100% 0px 100% 0px' }
    );
    observedElements.forEach(node => observer.observe(node));
  }

  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);

  return () => {
    stopLoop();
    observer?.disconnect();
    window.removeEventListener('resize', update);
    window.removeEventListener('orientationchange', update);
    words.forEach(word => {
      word.style.opacity = '';
    });
  };
};

export default function ParagraphRevealStandalone({
  children,
  className = '',
  animate = true,
  start = 'top 85%',
  end = 'top 60%',
  staggerEach = 0.04,
  wordDuration = 0.3,
  delay = 0,
  once = false,
  dimOpacity = 0.1,
  contentKey,
}) {
  const paragraphRef = useRef(null);
  const animationKey = contentKey != null ? contentKey : children;
  const shouldAnimate = animate && children != null && children !== false;

  const processedChildren = useMemo(
    () => (shouldAnimate ? wrapWordsInChildren(children) : children),
    // contentKey is the stable identity of the copy; children is recreated every
    // parent render (markdown components included) and must not restart this.
    [shouldAnimate, animationKey]
  );

  useLayoutEffect(() => {
    if (!shouldAnimate) return undefined;

    const element = paragraphRef.current;
    if (!element) return undefined;

    return animateWords({
      element,
      start,
      end,
      staggerEach,
      wordDuration,
      delay,
      once,
      dimOpacity,
    });
  }, [
    shouldAnimate,
    start,
    end,
    staggerEach,
    wordDuration,
    delay,
    once,
    dimOpacity,
    animationKey,
  ]);

  if (!shouldAnimate) {
    return children;
  }

  return (
    <div ref={paragraphRef} className={classNames(styles.paragraphReveal, className)}>
      {processedChildren}
    </div>
  );
}
